import { Alert, Box, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAddContentMaterialsBulk, useContentManagerId } from '../../api';
import { useContentExit } from '../ContentBackLink';
import { useContentExitQueue } from '../ContentExitContext';

import { ScriptRow } from './ScriptRow';
import { ScriptDraft } from './types';
import { createEmptyRow, isRowFilled, validateRows } from './utils';

import { Departments } from '@/types';
import { COLORS } from '@/utils/colors';

type Props = {
  department: Departments;
  pageId: string;
};

export const AddScriptsForm = ({ department, pageId }: Props) => {
  const navigate = useNavigate();
  const {
    managerId,
    isReady,
    needsManager,
    isLoading: isAuthLoading,
  } = useContentManagerId(department);
  const addMaterialsBulk = useAddContentMaterialsBulk();

  const [rows, setRows] = useState<ScriptDraft[]>(() => [createEmptyRow()]);
  const [errors, setErrors] = useState<Record<string, { name?: string; textContent?: string }>>({});
  const [submitted, setSubmitted] = useState(false);

  const filledCount = useMemo(() => rows.filter(isRowFilled).length, [rows]);
  const hasQueue = filledCount > 0;

  useContentExitQueue(hasQueue);
  const tryExit = useContentExit(hasQueue);

  const updateRow = (id: string, patch: Partial<ScriptDraft>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));

    if (submitted) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const removeRow = (id: string) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((row) => row.id !== id);
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleSave = async () => {
    setSubmitted(true);

    const nextErrors = validateRows(rows);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || !isReady) {
      return;
    }

    const toSave = rows.filter(isRowFilled).map((row) => ({
      pageId,
      type: 'text' as const,
      name: row.name.trim(),
      category: row.category.trim(),
      status: 'active' as const,
      filename: '',
      size: 0,
      textContent: row.textContent.trim(),
    }));

    if (toSave.length === 0) {
      return;
    }

    try {
      await addMaterialsBulk.mutateAsync({ items: toSave, managerId });
      navigate('/content');
    } catch {
      // Error toast is shown by the mutation hook
    }
  };

  return (
    <Stack gap="md">
      {needsManager && (
        <Alert color="yellow" title="Нужен менеджер">
          Создайте менеджера в разделе Managers для региона {department.toUpperCase()}, чтобы
          сохранять контент.
        </Alert>
      )}

      <Paper
        p="md"
        radius="md"
        style={{
          border: `1px solid ${COLORS.primary}`,
          backgroundColor: 'var(--mantine-color-dark-7)',
        }}
      >
        <Group justify="space-between" mb="md">
          <Text size="sm" c="dimmed">
            Заполнено: {filledCount}
          </Text>
          <Button variant="default" size="xs" leftSection={<IconPlus size={14} />} onClick={addRow}>
            Добавить строку
          </Button>
        </Group>

        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: '32px minmax(180px, 1fr) minmax(280px, 2fr) 200px 40px',
            gap: 12,
            paddingBottom: 8,
            opacity: 0.7,
          }}
        >
          <Text size="xs" tt="uppercase">
            #
          </Text>
          <Text size="xs" tt="uppercase">
            Название
          </Text>
          <Text size="xs" tt="uppercase">
            Текст
          </Text>
          <Text size="xs" tt="uppercase">
            Категория
          </Text>
          <span />
        </Box>

        {rows.map((row, index) => (
          <ScriptRow
            key={row.id}
            pageId={pageId}
            index={index + 1}
            row={row}
            errors={errors[row.id]}
            canRemove={rows.length > 1}
            onChange={(patch) => updateRow(row.id, patch)}
            onRemove={() => removeRow(row.id)}
          />
        ))}
      </Paper>

      <Group justify="flex-end">
        <Button variant="default" onClick={tryExit}>
          Отмена
        </Button>
        <Button
          onClick={handleSave}
          loading={addMaterialsBulk.isLoading}
          disabled={filledCount === 0 || !isReady || isAuthLoading}
        >
          Сохранить ({filledCount})
        </Button>
      </Group>
    </Stack>
  );
};
